/**
 * Shared HTTP helpers for the ally Partner API.
 *
 * Centralises URL joining, query-string building, token fetching, and error
 * normalisation so each `use_*_query` hook can stay focused on its endpoint
 * shape.
 */

/** Serialisable query-string values (undefined/null entries are omitted). */
export type QueryParams = Record<string, string | number | boolean | null | undefined>;

export const joinUrl = (baseUrl: string, path: string): string => {
  const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
  return `${normalizedBase}/${normalizedPath}`;
};

/**
 * Serialise params into a `?a=1&b=2` suffix, or `''` when there is nothing to
 * add.
 *
 * Null and undefined values are dropped rather than sent empty, so an unset
 * filter is absent from the URL entirely — which matters because callers fold
 * the same params into their React Query cache key.
 */
export const toQueryString = (params?: QueryParams): string => {
  if (!params) return '';

  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value != null) query.set(key, String(value));
  });

  return query.size > 0 ? `?${query}` : '';
};

export type GetAccessToken = (opts?: {
  authorizationParams?: { audience?: string; scope?: string; organization?: string };
}) => Promise<string>;

/**
 * Build an `Authorization` header for an API audience.
 *
 * `scope` exists for the My Organization API, whose permissions are
 * per-operation and so must be named in the token request. The ally Partner API
 * derives them from the caller's roles and passes nothing — omitting `scope`
 * reproduces the exact call shape the ally hooks have always made, so their
 * entry in the Auth0 SDK's token cache is unaffected.
 */
export const getAuthHeader = async (
  getAccessTokenSilently: GetAccessToken,
  auth0Audience: string | undefined,
  scope?: string
): Promise<string> => {
  if (!auth0Audience) {
    return `Bearer ${await getAccessTokenSilently()}`;
  }

  const authorizationParams = scope
    ? { audience: auth0Audience, scope }
    : { audience: auth0Audience };

  return `Bearer ${await getAccessTokenSilently({ authorizationParams })}`;
};

/**
 * A non-2xx API response, carrying the status so callers can branch on it.
 *
 * The message is formatted exactly as it was when this was a plain `Error`, so
 * anything matching on it still works; `status` is what lets a caller tell an
 * expected refusal (403 for a role that lacks a scope) from a transient failure
 * (429, 5xx) instead of pattern-matching prose.
 */
export class ApiError extends Error {
  readonly status: number;

  readonly statusText: string;

  /** `Retry-After` in seconds, when the response gave it as a number. */
  readonly retryAfter: number | null;

  /**
   * The parsed JSON error body, when the response was valid JSON — e.g. the
   * edit-role endpoint's `{ partiallyApplied: true }` (FR-013). `undefined`
   * when the body was empty or not JSON, so callers must check before use.
   */
  readonly body: unknown;

  constructor(
    message: string,
    status: number,
    statusText: string,
    retryAfter: number | null,
    body?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.retryAfter = retryAfter;
    this.body = body;
  }
}

/**
 * Read `Retry-After` as a whole number of seconds.
 *
 * The header may instead hold an HTTP date. That form is ignored rather than
 * converted: a value derived from the server's clock is only as good as the
 * agreement between the two clocks, and a caller that gets `null` can back off
 * on its own schedule instead.
 */
export const parseRetryAfter = (headers: Headers): number | null => {
  const raw = headers.get('Retry-After');
  if (!raw) return null;

  const seconds = Number(raw);
  return Number.isInteger(seconds) && seconds >= 0 ? seconds : null;
};

/**
 * `init` defaults to a plain GET — every read hook already calls this with no
 * fourth argument, so this stays behaviourally identical for them. Mutation
 * hooks pass `{ method: 'POST' | 'PATCH' | 'DELETE', body, headers }` to reuse
 * the same auth header handling and `ApiError` shape (status/retryAfter) that
 * the read side already has.
 */
export const fetchJson = async <T>(
  url: string,
  authHeader: string,
  errorContext: string,
  init?: RequestInit
): Promise<T> => {
  const response = await fetch(url, {
    ...init,
    method: init?.method ?? 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: authHeader,
      ...(init?.headers as Record<string, string> | undefined),
    },
  });

  if (!response.ok) {
    const responseText = await response.text().catch(() => '');
    let body: unknown;
    try {
      body = responseText ? JSON.parse(responseText) : undefined;
    } catch {
      body = undefined;
    }
    throw new ApiError(
      `${errorContext}: ${response.status} ${response.statusText}${
        responseText ? ` - ${responseText}` : ''
      }`,
      response.status,
      response.statusText,
      parseRetryAfter(response.headers),
      body
    );
  }

  // DELETE routes in this API answer 204 with no body — nothing to parse.
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
};

/**
 * A user-facing message for a failed invite/edit-role/remove mutation
 * (FR-019/020). Never surfaces the raw status text or JSON body — those are
 * for logs/DevTools, not the Admin — and never phrases a 502/503 as a
 * permissions problem: it means ally's own M2M credential is misconfigured,
 * not that the Admin lacks a role (ally's RBAC already let the call through
 * before it ever reached Auth0).
 *
 * `notFound` lets a caller supply its own wording for the 404 case (FR-021)
 * — "member" vs. "invitation" reads differently depending on the modal —
 * and is optional because the Invite modal has no existing target to go
 * stale, so it never has one to pass.
 */
export const describeMutationError = (
  error: unknown,
  {
    notFound,
    codeMessages,
    fallback,
  }: { notFound?: string; codeMessages?: Record<string, string>; fallback: string }
): string => {
  if (error instanceof ApiError) {
    if (error.status === 429) {
      return error.retryAfter
        ? `Too many requests. Please wait ${error.retryAfter}s and try again.`
        : 'Too many requests. Please wait a moment and try again.';
    }
    if (error.status === 502 || error.status === 503) {
      return "This isn't available right now. Please try again shortly.";
    }
    if (error.status === 404 && notFound) {
      return notFound;
    }
    // A structured `{ code, error }` body (e.g. the API Keys 2-key-limit /
    // 1-key-floor refusals, sc-45103) lets a caller supply its own copy per
    // code, same idea as `notFound` above for the 404 case.
    const code =
      error.body && typeof error.body === 'object' && 'code' in error.body
        ? (error.body as { code?: unknown }).code
        : undefined;
    if (typeof code === 'string' && codeMessages?.[code]) {
      return codeMessages[code];
    }
  }
  return error instanceof Error ? error.message : fallback;
};

/**
 * Format a `Date` as a `YYYY-MM-DD` UTC string for date-range query params.
 * Both bounds are inclusive on the backend side.
 */
export const toIsoDate = (d: Date): string => {
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};
